import { useDispatch, useSelector } from "react-redux";
import ColorizeIcon from "@mui/icons-material/Colorize";
import { selectUsedProps } from "../../../app/slices/PropLibrarySlice";
import usePropHelper from "../../../hooks/PropHelper";
import { SequenceNodeProps } from "./SequenceNodeProps";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import { useMemo } from "react";
import {
  clickIndicatorStyles,
  IClickIndicator,
} from "../../../models/interactions/ClickIndicator";
import NodeAllPropsDropdown from "../components/NodeAllPropsDropdown";
import NodeStringDropdown from "../components/NodeStringDropdown";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import { applyPickedPropToInteraction } from "./SequencePropPicker.helpers";
import {
  NODE_PICK_BUTTON_ACTIVE,
  NODE_PICK_BUTTON_BASE,
  NODE_PICK_BUTTON_IDLE,
} from "../components/nodeStyles";

export default function ClickIndicatorNodePreview(props: SequenceNodeProps) {
  const propInteraction = props.interaction as IClickIndicator;
  const { tryGetUsedPropByInstanceId } = usePropHelper();
  const usedProps = useSelector(selectUsedProps);
  const { updateInteraction } = useUpdateSequenceInteraction();
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);

  const usedProp = useMemo(() => {
    if (!propInteraction.propGuid) return null;
    return tryGetUsedPropByInstanceId(propInteraction.propGuid);
  }, [propInteraction.propGuid, usedProps, tryGetUsedPropByInstanceId]);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "prop";

  const propDropdown = useMemo(
    () => (
      <NodeAllPropsDropdown
        label="Prop"
        selectedPropStateId={propInteraction.propGuid}
        accessory={
          <button
            type="button"
            aria-label="Pick on map"
            title="Pick on map"
            data-testid="pick-prop-on-map-button"
            className={
              NODE_PICK_BUTTON_BASE +
              (isPickerActiveForNode
                ? NODE_PICK_BUTTON_ACTIVE
                : NODE_PICK_BUTTON_IDLE)
            }
            onClick={() =>
              dispatch(
                openSequencePositionPicker({
                  nodeId: props.nodeId,
                  positionType: "prop",
                  title: "Pick prop",
                  presentation: "splitView",
                }),
              )
            }
          >
            <ColorizeIcon fontSize="inherit" className="text-[18px]" />
          </button>
        }
        onClick={(propGuid) => {
          const prop = { id: propGuid } as any;
          updateInteraction(props.nodeId, {
            ...applyPickedPropToInteraction(
              propInteraction,
              prop,
              tryGetUsedPropByInstanceId,
            ),
          } as IClickIndicator);
        }}
      />
    ),
    [
      dispatch,
      tryGetUsedPropByInstanceId,
      isPickerActiveForNode,
      propInteraction,
      props.nodeId,
      updateInteraction,
    ]
  );

  const tagDropdown = useMemo(
    () => (
      <NodeStringDropdown
        items={usedProp?.metadata.clickableTags.map((p) => p.name) ?? []}
        selectedItem={propInteraction.tag}
        label="Tag"
        onClick={(tag) => {
          updateInteraction(props.nodeId, {
            ...propInteraction,
            tag: tag,
          } as IClickIndicator);
        }}
      />
    ),
    [usedProp, propInteraction.propGuid, updateInteraction, props.nodeId]
  );

  const stylesDropdown = useMemo(
    () => (
      <NodeStringDropdown
        items={clickIndicatorStyles}
        selectedItem={propInteraction.style}
        label="Style"
        onClick={(style) => {
          updateInteraction(props.nodeId, {
            ...propInteraction,
            style: style,
          } as IClickIndicator);
        }}
      />
    ),
    [usedProp, propInteraction.propGuid, updateInteraction, props.nodeId]
  );

  return (
    <div className="flex flex-col w-full h-full gap-[12px] noDrag">
      {propDropdown}
      {tagDropdown}
      {stylesDropdown}
    </div>
  );
}
